import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import App from "./App.tsx";
import "./index.css";
import { AuthProvider } from "./features/auth/context/AuthContext";
import { supabase } from "./lib/supabase";
import { setupGlobalErrorHandling } from "./lib/errorHandlers";
import { analyticsService } from "@/services/AnalyticsService";
import { queryClient } from "@/lib/queryClient";
import { HelmetProvider } from 'react-helmet-async';
import { logger } from "@/lib/logger";
import { initDevLogger } from "@/lib/devLogger";

// Initialize development logging bridge
initDevLogger();

// Initialize error tracking
setupGlobalErrorHandling();

// Track Core Web Vitals for performance monitoring
if (typeof window !== "undefined") {
  import("web-vitals").then(({ onCLS, onINP, onLCP, onFCP, onTTFB }) => {
    onCLS((metric) =>
      analyticsService.track("web_vital", {
        name: "CLS",
        value: metric.value,
        rating: metric.rating,
        id: metric.id,
      }),
    );

    onINP((metric) =>
      analyticsService.track("web_vital", {
        name: "INP",
        value: metric.value,
        rating: metric.rating,
        id: metric.id,
      }),
    );

    onLCP((metric) =>
      analyticsService.track("web_vital", {
        name: "LCP",
        value: metric.value,
        rating: metric.rating,
        id: metric.id,
      }),
    );

    onFCP((metric) =>
      analyticsService.track("web_vital", {
        name: "FCP",
        value: metric.value,
        rating: metric.rating,
        id: metric.id,
      }),
    );

    onTTFB((metric) =>
      analyticsService.track("web_vital", {
        name: "TTFB",
        value: metric.value,
        rating: metric.rating,
        id: metric.id,
      }),
    );
  });
}

// Clean URL hash if session is established and hash contains auth tokens or is just empty hash
// We do this before rendering to avoid the "flash" of the token in the URL
supabase.auth.getSession().then(({ data: { session } }) => {
  if (
    session &&
    window.location.hash &&
    (window.location.hash.includes("access_token") ||
      window.location.hash.includes("error_description") ||
      window.location.hash === "#")
  ) {
    // Explicitly remove the hash part to ensure no trailing # remains
    window.history.replaceState(null, "", window.location.href.split("#")[0]);
  }
});

// Initial terminal verification
logger.info('Spirit Research Lab - Terminal Logging Initialized 🚀');

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
